import { getAuth } from "firebase/auth";

/**
 * Rent Reminder API Service
 * Handles all rent reminder related API calls
 */

const API_BASE_URL = "/api";

/**
 * Send individual tenant reminder
 * @param {string} tenantId - Tenant ID
 * @param {string} landlordId - Landlord ID
 * @param {string} [reminderType] - Type of reminder ('rent_due' | 'overdue' | 'late_fee')
 * @param {string} [customMessage] - Optional custom message
 * @param {Array} [deliveryMethods] - Delivery methods ['email', 'sms']
 * @param {string} [paymentId] - Optional payment ID to send reminder for specific payment
 */
export const sendIndividualReminder = async (
  tenantId,
  landlordId,
  reminderType = "rent_due",
  customMessage = null,
  deliveryMethods = ["email"],
  paymentId = null
) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Build request body
    const requestBody = {
      tenantId,
      landlordId,
      reminderType,
      customMessage,
      deliveryMethods,
    };

    // Add paymentId if provided
    if (paymentId) {
      requestBody.paymentId = paymentId;
    }

    const response = await fetch(`${API_BASE_URL}/rent/send-reminder/send`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await user.getIdToken()}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json();

      // Handle specific error cases
      if (
        errorData.message?.includes("payment not found") ||
        errorData.message?.includes("Payment not found")
      ) {
        throw new Error("Payment not found or does not belong to this tenant");
      }

      throw new Error(errorData.message || "Failed to send reminder");
    }

    return await response.json();
  } catch (error) {
    console.error("Error sending individual reminder:", error);
    throw error;
  }
};

/**
 * Send bulk owner reminders
 * @param {string} ownerId - Owner/Landlord ID
 */
export const sendBulkReminders = async (ownerId) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(
      `${API_BASE_URL}/rent-reminders/trigger/${ownerId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to send bulk reminders");
    }

    return await response.json();
  } catch (error) {
    console.error("Error sending bulk reminders:", error);
    throw error;
  }
};

/**
 * Send overdue payment reminders to all tenants
 * @param {string} landlordId - Landlord ID
 * @param {boolean} includeLateFee - Whether to include late fee calculations
 * @param {Array} deliveryMethods - Delivery methods ['email', 'sms']
 */
export const sendAllOverdueReminders = async (
  landlordId,
  includeLateFee = true,
  deliveryMethods = ["email"]
) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(
      `${API_BASE_URL}/overdue-payment-reminders/send`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
        body: JSON.stringify({
          landlordId,
          reminderType: "overdue_with_late_fee",
          includeLateFee,
          deliveryMethods,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to send overdue reminders");
    }

    return await response.json();
  } catch (error) {
    console.error("Error sending all overdue reminders:", error);
    throw error;
  }
};

/**
 * Send overdue payment reminder to specific tenant
 * @param {string} tenantId - Tenant ID
 * @param {string} landlordId - Landlord ID
 * @param {string} customMessage - Optional custom message
 * @param {boolean} includeLateFee - Whether to include late fee calculations
 * @param {Array} deliveryMethods - Delivery methods ['email', 'sms']
 */
export const sendOverdueReminderToTenant = async (
  tenantId,
  landlordId,
  customMessage = null,
  includeLateFee = true,
  deliveryMethods = ["email"]
) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(
      `${API_BASE_URL}/overdue-payment-reminders/send`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
        body: JSON.stringify({
          tenantId,
          landlordId,
          reminderType: "overdue_only",
          customMessage,
          includeLateFee,
          deliveryMethods,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to send overdue reminder");
    }

    return await response.json();
  } catch (error) {
    console.error("Error sending overdue reminder to tenant:", error);
    throw error;
  }
};

/**
 * Get reminder history for a landlord
 * @param {string} ownerId - Owner/Landlord ID
 */
export const getReminderHistory = async (ownerId) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(
      `${API_BASE_URL}/rent-reminders/history/${ownerId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to get reminder history");
    }

    return await response.json();
  } catch (error) {
    console.error("Error getting reminder history:", error);
    throw error;
  }
};

/**
 * Error handler for reminder operations
 * @param {Error} error - The error object
 * @param {Function} showErrorMessage - Function to show error message
 * @param {Function} showWarningMessage - Function to show warning message
 */
export const handleReminderError = (
  error,
  showErrorMessage,
  showWarningMessage
) => {
  if (error.message.includes("No overdue payments")) {
    showWarningMessage("This tenant has no overdue payments");
  } else if (error.message.includes("No email address")) {
    showErrorMessage("Tenant email address not found");
  } else if (error.message.includes("permission")) {
    showErrorMessage(
      "You don't have permission to send reminders to this tenant"
    );
  } else if (error.message.includes("No overdue payments found")) {
    showWarningMessage("No tenants have overdue payments");
  } else {
    showErrorMessage("Failed to send reminder. Please try again.");
  }
};

/**
 * Success message formatter for reminder operations
 * @param {Object} result - API response result
 * @param {string} type - Type of reminder ('individual' | 'bulk' | 'overdue')
 */
export const formatReminderSuccessMessage = (result, type = "individual") => {
  switch (type) {
    case "individual":
      return `Reminder sent to ${result.data?.tenantName || "tenant"}`;
    case "bulk":
      return `${result.data?.sent || 0} reminders sent successfully`;
    case "overdue":
      return `${result.data?.sent || 0} overdue reminders sent successfully`;
    default:
      return "Reminder sent successfully";
  }
};
